import React, { useCallback, useEffect, useState } from "react";
import { Download, Filter, RefreshCw, Search, ShieldAlert, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";

const PAGE_SIZE = 100;
const EMPTY_FILTERS = {
  action: "",
  actor: "",
  target: "",
  notificationStatus: "",
  dateFrom: "",
  dateTo: "",
};

const dateBoundary = (value, end = false) => {
  if (!value) return undefined;
  return new Date(`${value}T${end ? "23:59:59.999" : "00:00:00.000"}`).toISOString();
};

const buildParams = (filters, skip = 0) => ({
  action: filters.action || undefined,
  actor: filters.actor || undefined,
  target: filters.target || undefined,
  notification_status: filters.notificationStatus || undefined,
  date_from: dateBoundary(filters.dateFrom),
  date_to: dateBoundary(filters.dateTo, true),
  limit: PAGE_SIZE,
  skip,
});

const Stat = ({ label, value, alert = false }) => (
  <div className="kd9-card p-4">
    <div className="font-mont text-[9px] uppercase tracking-[.18em]" style={{ color: "var(--kd9-text-soft)" }}>
      {label}
    </div>
    <div className="font-cinzel text-2xl mt-2" style={{ color: alert ? "#FCA5A5" : "var(--kd9-gold)" }}>
      {value}
    </div>
  </div>
);

const AuditLogs = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    events: 0,
    notification_failures: 0,
    integrity: { valid: 0, invalid: 0, legacy: 0, sampled: 0 },
  });
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/director/audit", { params: buildParams(applied, skip) });
      setRows(data.items || []);
      setSummary((current) => data.summary || current);
      setTotal(Number(data.total || 0));
      setHasMore(Boolean(data.has_more));
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [applied, skip]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  const applyFilters = () => {
    setSkip(0);
    setApplied({ ...filters });
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSkip(0);
    setApplied(EMPTY_FILTERS);
  };

  const exportCsv = async () => {
    setExporting(true);
    setError("");
    try {
      const params = {
        action: applied.action || undefined,
        actor: applied.actor || undefined,
        target: applied.target || undefined,
        notification_status: applied.notificationStatus || undefined,
        date_from: dateBoundary(applied.dateFrom),
        date_to: dateBoundary(applied.dateTo, true),
      };
      const response = await api.get("/api/director/reports/audit.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `auditoria-kd9-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageShell
      kicker="Director KD9 · Gobierno"
      title="CENTRO DE AUDITORÍA"
      desc="Trazabilidad central de acciones críticas, actores, objetivos, notificaciones e integridad del registro."
      testId="audit-logs-page"
      badge={
        <button onClick={load} className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px]">
          <RefreshCw size={12} className="inline" /> Recargar
        </button>
      }
    >
      {error && <div className="kd9-card p-4 mb-5" style={{ color: "#FCA5A5" }}>{error}</div>}

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5" data-testid="audit-summary">
        <Stat label="Eventos filtrados" value={summary.events || 0} />
        <Stat label="Fallos de notificación" value={summary.notification_failures || 0} alert={summary.notification_failures > 0} />
        <Stat label="Sellos inválidos" value={summary.integrity?.invalid || 0} alert={summary.integrity?.invalid > 0} />
        <Stat label="Registros históricos" value={summary.integrity?.legacy || 0} />
      </div>

      <div className="kd9-card p-4 mb-5" data-testid="audit-filters">
        <div className="flex items-center gap-2 mb-4 font-cinzel kd9-gold-text">
          <Filter size={16} /> Filtros de gobierno
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          <label className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              className="kd9-input pl-9"
              placeholder="Acción: premium, marketplace..."
              value={filters.action}
              onChange={(e) => patch("action", e.target.value)}
            />
          </label>
          <input
            className="kd9-input"
            placeholder="Actor: nombre, correo o ID"
            value={filters.actor}
            onChange={(e) => patch("actor", e.target.value)}
          />
          <input
            className="kd9-input"
            placeholder="Objetivo / entidad"
            value={filters.target}
            onChange={(e) => patch("target", e.target.value)}
          />
          <select
            className="kd9-input"
            value={filters.notificationStatus}
            onChange={(e) => patch("notificationStatus", e.target.value)}
          >
            <option value="">Todas las notificaciones</option>
            <option value="dispatched">Notificación enviada</option>
            <option value="pending">Notificación pendiente</option>
            <option value="failed">Notificación fallida</option>
          </select>
          <input
            className="kd9-input"
            type="date"
            aria-label="Fecha inicial"
            value={filters.dateFrom}
            onChange={(e) => patch("dateFrom", e.target.value)}
          />
          <input
            className="kd9-input"
            type="date"
            aria-label="Fecha final"
            value={filters.dateTo}
            onChange={(e) => patch("dateTo", e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={applyFilters} className="kd9-btn-primary px-4 py-2 rounded-lg text-[10px]">
            Aplicar filtros
          </button>
          <button onClick={clearFilters} className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px]">
            Limpiar
          </button>
          <button
            onClick={exportCsv}
            disabled={exporting}
            className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] disabled:opacity-50"
          >
            <Download size={12} className="inline" /> {exporting ? "Exportando..." : "Exportar CSV"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>
        <span>{total} eventos · muestra de integridad: {summary.integrity?.sampled || 0}</span>
        <span>Página {Math.floor(skip / PAGE_SIZE) + 1}</span>
      </div>

      <div className="kd9-card overflow-hidden">
        {rows.map((log) => (
          <article key={log.id} className="p-5 border-b last:border-0" style={{ borderColor: "var(--kd9-border)" }}>
            <div className="flex flex-wrap gap-3 items-center">
              {log.integrity === "invalid"
                ? <ShieldAlert size={17} style={{ color: "#FCA5A5" }} />
                : <ShieldCheck size={17} style={{ color: "var(--kd9-gold)" }} />}
              <div className="font-cinzel kd9-gold-text flex-1 min-w-[180px]">{log.action}</div>
              <time className="font-mont text-[10px]">
                {log.at ? new Date(log.at).toLocaleString("es-CO") : ""}
              </time>
              <span className="rounded-full border border-white/10 px-2 py-1 text-[9px]">
                {log.integrity === "valid" ? "Sello válido" : log.integrity === "invalid" ? "ALERTA DE INTEGRIDAD" : "Registro histórico"}
              </span>
              <span className="rounded-full border border-white/10 px-2 py-1 text-[9px]">
                {log.notification_status === "failed" ? "Notificación fallida" : log.notification_status === "pending" ? "Notificación pendiente" : "Notificación procesada"}
              </span>
            </div>
            <div className="font-mont text-[10px] mt-2" style={{ color: "var(--kd9-text-soft)" }}>
              Actor: {log.actor_name || log.actor_email || log.actor_id || "sistema"} · Objetivo: {log.target || "—"}
            </div>
            {log.notification_error && (
              <div className="font-mont text-[10px] mt-2" style={{ color: "#FCA5A5" }}>
                Error de notificación: {log.notification_error}
              </div>
            )}
            {Object.keys(log.payload || {}).length > 0 && (
              <details className="mt-3">
                <summary className="font-mont text-[10px] cursor-pointer">Ver cambios</summary>
                <pre className="mt-2 p-3 rounded overflow-auto text-[10px]" style={{ background: "rgba(0,0,0,.25)" }}>
                  {JSON.stringify(log.payload, null, 2)}
                </pre>
              </details>
            )}
          </article>
        ))}
      </div>

      {!rows.length && <div className="kd9-card p-10 text-center font-cormorant text-xl">No hay eventos con esos filtros.</div>}

      <div className="flex justify-end gap-2 mt-4">
        <button
          className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] disabled:opacity-40"
          disabled={skip === 0}
          onClick={() => setSkip((value) => Math.max(0, value - PAGE_SIZE))}
        >
          Anterior
        </button>
        <button
          className="kd9-btn-outline px-4 py-2 rounded-lg text-[10px] disabled:opacity-40"
          disabled={!hasMore}
          onClick={() => setSkip((value) => value + PAGE_SIZE)}
        >
          Siguiente
        </button>
      </div>
    </PageShell>
  );
};

export default AuditLogs;
